import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import clsx from 'clsx';

interface DateRangePickerProps {
  fromDate: string;
  toDate: string;
  onChange: (fromDate: string, toDate: string) => void;
  className?: string;
}

type PresetRange = 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'last3months';

const PRESETS: PresetRange[] = ['last7days', 'last30days', 'thisMonth', 'lastMonth', 'last3months'];

function presetRange(range: PresetRange, today = new Date()): [string, string] {
  let from: Date;
  let to: Date = today;
  switch (range) {
    case 'last7days':
      from = subDays(today, 7);
      break;
    case 'last30days':
      from = subDays(today, 30);
      break;
    case 'thisMonth':
      from = startOfMonth(today);
      to = endOfMonth(today);
      break;
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1);
      from = startOfMonth(lastMonth);
      to = endOfMonth(lastMonth);
      break;
    }
    case 'last3months':
      from = subMonths(today, 3);
      break;
  }
  return [format(from, 'yyyy-MM-dd'), format(to, 'yyyy-MM-dd')];
}

export function DateRangePicker({ fromDate, toDate, onChange, className }: DateRangePickerProps) {
  const { t } = useTranslation();
  const id = useId();

  // Derive the highlighted preset from the current range so the chip always matches
  // whatever range the parent initialised with (e.g. 3 months on government reports).
  const activePreset = PRESETS.find((p) => {
    const [from, to] = presetRange(p);
    return from === fromDate && to === toDate;
  });

  const invalid = Boolean(fromDate && toDate && fromDate > toDate);

  return (
    <div className={clsx('card p-4', className)}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('charts.period')}>
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(...presetRange(preset))}
              aria-pressed={activePreset === preset}
              className={clsx(
                'rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                activePreset === preset
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              {t(`charts.presets.${preset}`)}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:flex sm:items-end">
          <div>
            <label htmlFor={`${id}-from`} className="label">
              {t('charts.from')}
            </label>
            <input
              id={`${id}-from`}
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => e.target.value && onChange(e.target.value, toDate)}
              className={invalid ? 'input-error' : 'input'}
            />
          </div>
          <div>
            <label htmlFor={`${id}-to`} className="label">
              {t('charts.to')}
            </label>
            <input
              id={`${id}-to`}
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => e.target.value && onChange(fromDate, e.target.value)}
              className={invalid ? 'input-error' : 'input'}
            />
          </div>
        </div>
      </div>
      {invalid && <p className="field-error">{t('charts.invalidRange')}</p>}
    </div>
  );
}
